<template>
  <div
    role="article"
    aria-label="Audio Block"
    class="audio-block"
  >
    <!-- Title & Description -->
    <div class="audio-block__content">
      <div class="audio-block__title-wrapper">
        <div class="audio-block__title-header">
          <div class="audio-block__title-icon">
            <img
              class="audio-block__volumne-icon"
              :src="`/themes/custom/education_base/assets/images/icon_volume_up.svg`"
              alt=""
            >
          </div>
          <div class="audio-block__audio-download">
            <a
              :href="audioUrl"
              aria-label="Download audio file"
              class="
                c-button
                c-button--bold
                c-button--download-icon
                c-button--transparent
              "
            >
              <span class="c-button__label">
                <span>Download</span>
                <img
                  src="/themes/custom/education_base/assets/images/icon-download-blue.svg"
                  alt="Download"
                  class="c-button__icon-blue"
                >
              </span>
              <small
                class="c-button__hint"
              >{{ audioFileExtension }} | {{ audioFileSize }}</small>
            </a>
          </div>
        </div>
        <div class="audio-block__title">
          {{ title }}
        </div>
      </div>
      <div
        v-if="description"
        class="audio-block__description"
      >
        {{ description }}
        <div
          v-if="description"
          class="audio-block__player"
        >
          <vue-plyr>
            <audio
              controls
              crossorigin
              playsinline
            >
              <source
                :src="audioUrl"
                type="audio/mp3"
              >
            </audio>
          </vue-plyr>
        </div>
      </div>
    </div>
    <!-- Transcript -->
    <div class="audio-block__transcript">
      <button
        :id="`transcript-button-${id}`"
        class="audio-block__transcript-button"
        :class="{'audio-block__transcript-button--expanded': showTranscript}"
        aria-label="Toggle audio transcript"
        :aria-expanded="showTranscript"
        @click="toggleTranscript"
      >
        <span class="audio-block__transcript-label">
          <img
            class="audio-block__transcript-icon"
            :src="`/themes/custom/education_base/assets/images/icon-transcript.svg`"
            alt=""
          >
          <img
            class="audio-block__transcript-icon-blue"
            :src="`/themes/custom/education_base/assets/images/icon-transcript-blue.svg`"
            alt=""
          >
          <span> Audio transcript </span>
        </span>
        <img
          class="audio-block__transcript-close-btn"
          :src="`/themes/custom/education_base/assets/images/icon-close.svg`"
          alt=""
        >
        <img
          class="audio-block__transcript-close-btn-blue"
          :src="`/themes/custom/education_base/assets/images/icon-close-blue.svg`"
          alt=""
        >
      </button>
      <div
        class="audio-block__transcript-content"
        :class="{'audio-block__transcript-content--expanded': showTranscript}"
        :aria-hidden="!showTranscript"
      >
        <div
          class="audio-block__transcript-content-inner"
          :class="{
            'audio-block__transcript-content-inner--expanded': showTranscript,
          }"
        >
          <slot name="transcript" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AudioBlock',
  components: {},
  props: {
    /**
     * A unique number used to aid accessibility.
     */
    id: {
      type: Number,
      required: true,
    },
    /**
     * Title for the block
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Description for the block
     */
    description: {
      type: String,
      required: true,
    },
    /**
     * Url for audio to be embeded in an iframe
     */
    audioUrl: {
      type: String,
      required: true,
    },
    audioFileExtension: {
      type: String,
      required: true,
    },
    audioFileSize: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      showTranscript: false,
    };
  },
  mounted() {},
  methods: {
    /**
     * Used for expanding and collapsing the audio transcript
     */
    toggleTranscript() {
      this.showTranscript = !this.showTranscript;
    },
  },
};
</script>

<style scoped lang="scss">
/* stylelint-disable-next-line unit-disallowed-list */
$size-thumbnail-width: 347px;
/* stylelint-disable-next-line unit-disallowed-list */
$size-thumbnail-height: 196px;
/* stylelint-disable-next-line unit-disallowed-list */
$border-radius-small: 5px;

.audio-block {
  display: flex;
  flex-direction: column;

  @include education-breakpoint-tablet-down {
    flex-direction: column;
  }

  &__title-header {
    display: flex;
    margin-bottom: 1.5rem;
  }

  &__volumne-icon {
    width: 2.5rem;
  }

  &__close {
    display: flex;
    justify-content: flex-start;

    @include hover-focus-active {
      .audio-block__transcript-close-btn {
        display: none;
      }

      .audio-block__transcript-close-btn-blue {
        display: inline-block;
      }
    }

    @include focus-active {
      @include focus-outline;
    }
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    margin: 0 0 1rem 0;
    padding: 1.6rem 2rem 1rem 2rem;
    box-sizing: border-box;
    background-color: $color-white;
    border: $border-width solid $color-grey-s6;
    border-radius: $border-radius-small;

    .audio-block & {
      @include education-breakpoint-tablet-down() {
        width: 100%;
      }
    }
  }

  &__title {
    @include heading-x-small;

    display: inline-block;
    width: 100%;
    margin: 0.5rem 0 1rem 0;
    box-sizing: border-box;
    background: $color-white;
    font-weight: $font-weight-bold;
    pointer-events: none;
    color: $color-charcoal-mid-s3;

    &:hover {
      cursor: pointer;
    }

    @include focus-active {
      @include focus-outline;
    }

    @include education-breakpoint-tablet-down() {
      margin: 0 0 gap-size(0.5) 0;
    }

    &:not(:first-child) {
      margin-top: 0;
    }
  }

  &__title-icon {
    display: flex;
    flex-basis: 50%;
  }

  &__audio-download {
    flex-basis: 50%;
    display: flex;
    justify-content: flex-end;
  }

  &__title-wrapper {
    width: 100%;
  }

  &__description {
    @include text-small;

    width: 100%;
    color: $color-charcoal-mid-s3;
  }

  &__transcript {
    box-sizing: border-box;
    border: $border-width solid $color-grey-s6;
    border-radius: $border-radius-small;
    background-color: $color-white;
  }

  &__transcript-button {
    @include reset-button();

    position: relative;
    width: 100%;
    padding: gap-size(0.5) gap-size(0.7);
    box-sizing: border-box;
    height: 3.72rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include text-small;

    &--expanded {
      .audio-block__transcript-close-btn-blue {
        display: none;
      }

      .audio-block__transcript-close-btn,
      .audio-block__transcript-close-btn-blue {
        transform: rotate(45deg);
      }
    }

    .audio-block__transcript-icon-blue {
      display: none;
    }

    @include hover-focus-active {
      .audio-block__transcript-icon-blue {
        display: inline-block;
      }

      .audio-block__transcript-icon {
        display: none;
      }

      .audio-block__transcript-label {
        color: $color-blue-s3;
      }

      .audio-block__transcript-close-btn {
        display: none;
      }

      .audio-block__transcript-close-btn-blue {
        display: inline-block;
      }
    }

    @include focus-active {
      @include focus-outline;
    }
  }

  &__transcript-close-btn,
  &__transcript-close-btn-blue {
    transform: rotate(0deg);
    transition: transform $transition-duration $transition-timing;
  }

  &__transcript-close-btn-blue {
    display: none;
  }

  &__transcript-label {
    display: flex;

    @include text-small;

    font-size: 0.78rem;
  }

  &__transcript-icon,
  &__transcript-icon-blue {
    margin-right: gap-size(0.55);
  }

  &__transcript-content {
    p {
      @include text-small;
    }

    height: 0;
    overflow: hidden;
    transition: height $transition-duration $transition-timing;

    &--expanded {
      height: auto;
      margin-bottom: 1rem;
      padding: gap-size(0.5) gap-size(1.67);

      @include education-breakpoint-tablet-down {
        padding: 0 gap-size(0.7);
      }

      .audio-block__transcript-content-inner {
        transform: translate3d(0, 0, 0);
        opacity: 1;
      }
    }
  }

  &__transcript-content-inner {
    transition: $transition-expand;
    transform: translate3d(0, -1rem, 0);
    opacity: 0;
  }
}
</style>
